import { useState, useEffect, useMemo, useRef } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, timeToMin, minToTime, durationMin, pad, todayIso } from './lib/format.js';
import { ProjectAvatar } from './components.jsx';
import { CustomerAvatar } from './customers.jsx';
import { ProjectFormModal } from './new-project-modal.jsx';
import { IconCalendar, IconPlus, IconX, IconChevronRight } from './icons.jsx';

// Helpers for the day picker
function isoFromDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function shiftIso(iso, deltaDays) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + deltaDays);
  return isoFromDate(dt);
}
function formatDayLabel(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
}

// --- Grid configuration ------------------------------------------------------
const START_HOUR = 7;
const END_HOUR = 20;
const SLOT_MIN = 30;
const SLOTS_PER_HOUR = 60 / SLOT_MIN;
const TOTAL_SLOTS = (END_HOUR - START_HOUR) * SLOTS_PER_HOUR;
const SLOT_PX = 36;
const SNAP_MIN = 15;          // drag-resize snap granularity
const PX_PER_MIN = SLOT_PX / SLOT_MIN;
const DRAG_THRESHOLD_PX = 4;  // resize handles: distance below which we treat as click
const MOVE_THRESHOLD_PX = 8;  // body: decisive movement that engages move-drag before the hold delay
const HOLD_MS = 200;          // body: press-and-hold duration that arms move-mode (short click = open editor)

function slotToTime(slot) { return minToTime(START_HOUR * 60 + slot * SLOT_MIN); }
function timeToSlot(t) { return (timeToMin(t) - START_HOUR * 60) / SLOT_MIN; }
function snapDeltaMin(deltaY) {
  return Math.round((deltaY / PX_PER_MIN) / SNAP_MIN) * SNAP_MIN;
}
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// --- Entry block --------------------------------------------------------------
function EntryBlock({ entry, project, customer, onSelect, onUpdate, selected }) {
  const [drag, setDrag] = useState(null); // { mode: 'move'|'top'|'bottom', deltaMin: number }

  const startMinOrig = timeToMin(entry.from_time);
  const endMinOrig   = timeToMin(entry.to_time);
  const dayMinStart  = START_HOUR * 60;
  const dayMinEnd    = END_HOUR * 60;

  // Apply drag preview to display values, with all bounds enforced
  let startMin = startMinOrig, endMin = endMinOrig;
  if (drag) {
    const d = drag.deltaMin;
    if (drag.mode === 'move') {
      const dur = endMinOrig - startMinOrig;
      let s = startMinOrig + d;
      let t = endMinOrig + d;
      if (s < dayMinStart) { s = dayMinStart; t = s + dur; }
      if (t > dayMinEnd)   { t = dayMinEnd; s = t - dur; }
      startMin = s; endMin = t;
    } else if (drag.mode === 'top') {
      startMin = clamp(startMinOrig + d, dayMinStart, endMinOrig - SNAP_MIN);
    } else if (drag.mode === 'bottom') {
      endMin = clamp(endMinOrig + d, startMinOrig + SNAP_MIN, dayMinEnd);
    }
  }

  const top    = ((startMin - dayMinStart) / SLOT_MIN) * SLOT_PX;
  const height = ((endMin - startMin) / SLOT_MIN) * SLOT_PX - 2;
  const kind = entry.kind || (entry.project_id ? 'project' : 'support');
  const isMeeting = kind === 'meeting';
  const isSupport = kind === 'support';
  // Color picks: project → project color; meeting → customer color (if any) else neutral; support → gray
  const color = isSupport
    ? '#5B6172'
    : isMeeting
      ? (customer?.color || '#6366F1')
      : (project?.color || 'var(--accent)');
  const dragging = !!drag;

  function startInteraction(mode) {
    return (e) => {
      e.stopPropagation();
      e.preventDefault();
      const startY = e.clientY;
      let lastDelta = 0;
      let active    = false;  // drag actually engaged
      let holdTimer = null;

      function engage() {
        if (active) return;
        active = true;
        setDrag({ mode, deltaMin: lastDelta });
      }

      // Body: a long press (hold) arms move-mode even without moving.
      // Handles: resize engages on movement only.
      if (mode === 'move') holdTimer = setTimeout(engage, HOLD_MS);

      function onMove(ev) {
        const dy = ev.clientY - startY;
        lastDelta = snapDeltaMin(dy);
        if (!active) {
          // Decisive movement also engages (fast drag, and resize handles)
          const threshold = mode === 'move' ? MOVE_THRESHOLD_PX : DRAG_THRESHOLD_PX;
          if (Math.abs(dy) <= threshold) return;
          engage();
        }
        setDrag({ mode, deltaMin: lastDelta });
      }

      async function onUp() {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        if (holdTimer) { clearTimeout(holdTimer); holdTimer = null; }

        if (!active) {
          // Short click — open the editor (body only, not the handles)
          setDrag(null);
          if (mode === 'move') onSelect(entry.id);
          return;
        }

        // Compute final times under the same constraints we used during preview
        let newFrom = entry.from_time;
        let newTo   = entry.to_time;
        if (mode === 'move') {
          const dur = endMinOrig - startMinOrig;
          let s = startMinOrig + lastDelta;
          let t = endMinOrig + lastDelta;
          if (s < dayMinStart) { s = dayMinStart; t = s + dur; }
          if (t > dayMinEnd)   { t = dayMinEnd; s = t - dur; }
          newFrom = minToTime(s);
          newTo   = minToTime(t);
        } else if (mode === 'top') {
          const s = clamp(startMinOrig + lastDelta, dayMinStart, endMinOrig - SNAP_MIN);
          newFrom = minToTime(s);
        } else if (mode === 'bottom') {
          const t = clamp(endMinOrig + lastDelta, startMinOrig + SNAP_MIN, dayMinEnd);
          newTo = minToTime(t);
        }

        setDrag(null);

        if (newFrom !== entry.from_time || newTo !== entry.to_time) {
          try { await onUpdate({ from_time: newFrom, to_time: newTo }); }
          catch (err) { console.error('Failed to update entry', err); }
        } else if (mode === 'move') {
          // Drag that ended up changing nothing → treat as a click, open the editor
          onSelect(entry.id);
        }
      }

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    };
  }

  return (
    <div
      onMouseDown={startInteraction('move')}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', left: 6, right: 6,
        top: top + 1, height,
        background: isSupport
          ? 'repeating-linear-gradient(135deg, #4a4f5c, #4a4f5c 10px, #565b69 10px, #565b69 20px)'
          : `linear-gradient(135deg, ${color}ee, ${color}bb)`,
        borderRadius: 8,
        padding: '6px 10px',
        color: '#fff',
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        boxShadow: dragging
          ? `0 0 0 2px var(--ink), 0 16px 32px -8px ${color}`
          : selected
            ? `0 0 0 2px var(--ink), 0 8px 20px -8px ${color}`
            : `0 2px 8px -2px ${color}55`,
        overflow: 'hidden',
        transition: dragging ? 'none' : 'box-shadow 150ms, top 80ms ease, height 80ms ease',
        zIndex: dragging ? 10 : 1,
      }}
    >
      {/* Top resize handle */}
      <div
        onMouseDown={startInteraction('top')}
        style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 8,
          cursor: 'ns-resize',
        }}
        onMouseEnter={e => {
          const bar = e.currentTarget.firstChild;
          if (bar) bar.style.opacity = 1;
        }}
        onMouseLeave={e => {
          const bar = e.currentTarget.firstChild;
          if (bar) bar.style.opacity = 0;
        }}
      >
        <div style={{
          height: 3, marginTop: 1, marginLeft: 'auto', marginRight: 'auto', width: 32,
          background: 'rgba(255,255,255,0.7)', borderRadius: 2, opacity: 0,
          transition: 'opacity 120ms',
        }}/>
      </div>

      {/* Bottom resize handle */}
      <div
        onMouseDown={startInteraction('bottom')}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 8,
          cursor: 'ns-resize',
        }}
        onMouseEnter={e => {
          const bar = e.currentTarget.firstChild;
          if (bar) bar.style.opacity = 1;
        }}
        onMouseLeave={e => {
          const bar = e.currentTarget.firstChild;
          if (bar) bar.style.opacity = 0;
        }}
      >
        <div style={{
          height: 3, marginBottom: 1, marginTop: 4, marginLeft: 'auto', marginRight: 'auto', width: 32,
          background: 'rgba(255,255,255,0.7)', borderRadius: 2, opacity: 0,
          transition: 'opacity 120ms',
        }}/>
      </div>

      <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.92, fontFamily: 'var(--mono)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{minToTime(startMin).slice(0,5)} – {minToTime(endMin).slice(0,5)}</span>
        {isSupport && <span style={{ fontSize: 9.5, fontFamily: 'var(--sans)', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 }}>Begleitaufgabe</span>}
        {isMeeting && <span style={{ fontSize: 9.5, fontFamily: 'var(--sans)', textTransform: 'uppercase', letterSpacing: '0.08em', opacity: 0.8 }}>📅 Meeting</span>}
      </div>
      <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {entry.description || project?.title || (isSupport ? 'Begleitaufgabe' : isMeeting ? 'Meeting' : 'Ohne Titel')}
      </div>
      {height > 50 && (project || (isMeeting && customer)) && (
        <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {project?.title || (isMeeting && customer ? customer.name : '')}
        </div>
      )}
    </div>
  );
}

// --- Inline composer ---------------------------------------------------------
const MEETING_VALUE = '__meeting__';
const NEW_PROJECT_VALUE = '__new_project__';

function Composer({ slot, defaultEndSlot, customers, projects, onSave, onCancel }) {
  const from = slotToTime(slot);
  const [to, setTo] = useState(slotToTime(defaultEndSlot));
  const [mode, setMode] = useState('project'); // 'project' | 'support'
  const [customerId, setCustomerId] = useState('');
  const [projectId, setProjectId] = useState(''); // '' | uuid | MEETING_VALUE
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const descRef = useRef(null);

  // Projects scoped to selected customer
  const customerProjects = useMemo(
    () => projects.filter(p => p.customer_id === customerId && (p.status === 'active' || p.status === 'closing')),
    [projects, customerId]
  );

  const isMeeting = projectId === MEETING_VALUE;

  // Auto-pick if customer has exactly one project (don't override existing Meeting choice)
  useEffect(() => {
    if (mode !== 'project') return;
    if (projectId === MEETING_VALUE) return;
    if (customerProjects.length === 1) setProjectId(customerProjects[0].id);
    else if (!customerProjects.find(p => p.id === projectId)) setProjectId('');
  }, [customerId, customerProjects, mode]);

  useEffect(() => {
    descRef.current?.focus();
    const h = (e) => { if (e.key === 'Escape' && !showCreateProject) onCancel(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onCancel, showCreateProject]);

  const top = slot * SLOT_PX;
  const height = (defaultEndSlot - slot) * SLOT_PX - 2;

  const supportValid = mode === 'support' && desc.trim().length >= 3;
  const projectValid = mode === 'project' && !!projectId && !isMeeting;
  const meetingValid = mode === 'project' && isMeeting && desc.trim().length >= 3;
  const timeValid = timeToMin(to) > timeToMin(from);
  const canSave = timeValid && (supportValid || projectValid || meetingValid);

  async function save() {
    if (!canSave) return;
    setBusy(true); setErr(null);
    try {
      if (mode === 'support') {
        await onSave({
          project_id: null, customer_id: null, kind: 'support',
          from_time: from, to_time: to, description: desc.trim(),
        });
      } else if (isMeeting) {
        await onSave({
          project_id: null, customer_id: customerId || null, kind: 'meeting',
          from_time: from, to_time: to, description: desc.trim(),
        });
      } else {
        await onSave({
          project_id: projectId, customer_id: null, kind: 'project',
          from_time: from, to_time: to, description: desc,
        });
      }
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
      setBusy(false);
    }
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', left: 6, right: 6,
        top: top + 1, minHeight: height,
        background: 'var(--bg-card)',
        border: '2px solid var(--accent)',
        borderRadius: 10,
        padding: 10,
        boxShadow: 'var(--shadow-lg)',
        zIndex: 20,
      }}
    >
      {/* Time row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <span className="mono tiny" style={{ color: 'var(--ink-2)' }}>{from}</span>
        <span className="tiny muted">bis</span>
        <input
          type="time" value={to} onChange={(e) => setTo(e.target.value)}
          className="input" style={{ width: 100, padding: '4px 8px', fontSize: 12 }}
        />
        <div style={{ flex: 1 }}/>
        <button className="btn btn-ghost" style={{ padding: '2px 8px' }} onClick={onCancel}>
          <IconX size={12}/>
        </button>
      </div>

      {/* Mode toggle */}
      <div className="seg" style={{ width: '100%', marginBottom: 8 }}>
        <button
          className={mode === 'project' ? 'on' : ''}
          onClick={() => setMode('project')}
          style={{ flex: 1 }}
        >Projekt-Zeit</button>
        <button
          className={mode === 'support' ? 'on' : ''}
          onClick={() => setMode('support')}
          style={{ flex: 1 }}
        >Begleitaufgabe</button>
      </div>

      {mode === 'project' ? (
        <>
          <select
            className="select" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 6 }}
            value={customerId} onChange={(e) => setCustomerId(e.target.value)}
          >
            <option value="">Kunde wählen… (für Meeting optional)</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select
            className="select" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 6 }}
            value={projectId}
            onChange={(e) => {
              const v = e.target.value;
              if (v === NEW_PROJECT_VALUE) {
                setShowCreateProject(true);
              } else {
                setProjectId(v);
              }
            }}
          >
            <option value="">{customerId ? 'Projekt wählen…' : 'Auswahl treffen…'}</option>
            <option value={MEETING_VALUE}>📅 Meeting</option>
            {customerId && customerProjects.length > 0 && (
              <optgroup label="Projekte">
                {customerProjects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </optgroup>
            )}
            {customerId && customerProjects.length === 0 && (
              <option disabled>— Keine aktiven Projekte für diesen Kunden —</option>
            )}
            <option value={NEW_PROJECT_VALUE}>+ Neues Projekt anlegen…</option>
          </select>

          <input
            ref={descRef}
            className="input" style={{ padding: '6px 8px', fontSize: 12.5 }}
            placeholder={isMeeting ? 'Was war Inhalt des Meetings? (Pflicht)' : 'Was hast du gemacht? (optional)'}
            value={desc} onChange={(e) => setDesc(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy && canSave) save(); }}
          />
          {isMeeting && (
            <div className="tiny" style={{ color: meetingValid ? 'var(--ok)' : 'var(--ink-3)', marginTop: 4 }}>
              {desc.trim().length < 3
                ? `noch ${3 - desc.trim().length} Zeichen — Meeting-Inhalt ist Pflicht`
                : '✓ ausreichend'}
            </div>
          )}
        </>
      ) : (
        <>
          <input
            ref={descRef}
            className="input" style={{ padding: '6px 8px', fontSize: 12.5 }}
            placeholder="Was hast du gemacht? (Pflicht)"
            value={desc} onChange={(e) => setDesc(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy && canSave) save(); }}
          />
          <div className="tiny" style={{ color: supportValid ? 'var(--ok)' : 'var(--ink-3)', marginTop: 4 }}>
            {desc.trim().length < 3
              ? `noch ${3 - desc.trim().length} Zeichen — Pflichtfeld`
              : '✓ ausreichend'}
          </div>
        </>
      )}

      {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
        <button className="btn btn-ghost" style={{ padding: '4px 10px' }} onClick={onCancel}>Abbrechen</button>
        <button className="btn btn-primary" onClick={save}
                disabled={busy || !canSave}
                style={{ padding: '4px 12px', opacity: (canSave && !busy) ? 1 : 0.45 }}>
          {busy ? 'Speichere…' : 'Eintragen'}
        </button>
      </div>

      {showCreateProject && (
        <ProjectFormModal
          defaultCustomerId={customerId || undefined}
          onClose={() => setShowCreateProject(false)}
          onSaved={(newProjectId) => {
            if (newProjectId) setProjectId(newProjectId);
            setShowCreateProject(false);
          }}
        />
      )}
    </div>
  );
}

// --- Selected-block popover --------------------------------------------------
function EntryEditor({ entry, project, customers, projects, coworkers = [], onDelete, onClose, onUpdate, onShare }) {
  const initialKind = entry.kind || (entry.project_id ? 'project' : 'support');
  const [mode, setMode] = useState(initialKind === 'support' ? 'support' : 'project');
  const [customerId, setCustomerId] = useState(
    initialKind === 'meeting' ? (entry.customer_id || '') : (project?.customer_id || '')
  );
  const [projectId, setProjectId] = useState(
    initialKind === 'meeting' ? MEETING_VALUE : (entry.project_id || '')
  );
  const [desc, setDesc] = useState(entry.description || '');
  const [date, setDate] = useState(entry.entry_date);
  const [from, setFrom] = useState(entry.from_time.slice(0, 5));
  const [to, setTo] = useState(entry.to_time.slice(0, 5));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [shareWith, setShareWith] = useState([]);     // user-ids der Kolleg:innen
  const [sharing, setSharing] = useState(false);
  const [shareNote, setShareNote] = useState(null);

  const customerProjects = useMemo(
    () => projects.filter(p => p.customer_id === customerId && (p.status === 'active' || p.status === 'closing' || p.id === entry.project_id)),
    [projects, customerId, entry.project_id]
  );

  const isMeeting = projectId === MEETING_VALUE;
  const supportValid = mode === 'support' && desc.trim().length >= 3;
  const projectValid = mode === 'project' && !!projectId && !isMeeting;
  const meetingValid = mode === 'project' && isMeeting && desc.trim().length >= 3;
  const timeValid = timeToMin(to) > timeToMin(from);
  const canSave = !!date && timeValid && (supportValid || projectValid || meetingValid);

  // Aktuelle Editor-Werte als Eintragsdaten (für Speichern UND Teilen).
  function entryData() {
    const base = mode === 'support'
      ? { project_id: null, customer_id: null, kind: 'support', description: desc.trim() }
      : isMeeting
        ? { project_id: null, customer_id: customerId || null, kind: 'meeting', description: desc.trim() }
        : { project_id: projectId, customer_id: null, kind: 'project', description: desc };
    return { ...base, entry_date: date, from_time: from, to_time: to };
  }

  async function save() {
    if (!canSave) return;
    setBusy(true); setErr(null);
    try {
      await onUpdate(entryData());
      onClose();
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
      setBusy(false);
    }
  }

  function toggleShare(id) {
    setShareNote(null);
    setShareWith(xs => xs.includes(id) ? xs.filter(x => x !== id) : [...xs, id]);
  }

  async function share() {
    if (shareWith.length === 0 || !canSave) return;
    setSharing(true); setErr(null); setShareNote(null);
    try {
      const rows = await onShare({ user_ids: shareWith, ...entryData() });
      const n = rows.length;
      setShareNote(n === 0
        ? 'Alle Ausgewählten hatten diesen Eintrag bereits.'
        : `✓ an ${n} ${n === 1 ? 'Person' : 'Personen'} kopiert`);
      setShareWith([]);
    } catch (e) {
      setErr(e.message || 'Teilen fehlgeschlagen');
    } finally {
      setSharing(false);
    }
  }

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', right: -280, top: timeToSlot(entry.from_time) * SLOT_PX,
        width: 260,
        maxHeight: 'calc(100vh - 120px)', overflowY: 'auto',
        background: 'var(--bg-card)',
        border: '1px solid var(--stroke-2)',
        borderRadius: 12,
        padding: 12,
        boxShadow: 'var(--shadow-lg)',
        zIndex: 30,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{
          width: 28, height: 28, borderRadius: 7,
          background: `linear-gradient(135deg, ${project?.color || '#FF6B4A'}, ${project?.color || '#FF6B4A'}99)`,
        }}/>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600 }}>Eintrag bearbeiten</div>
          <div className="tiny muted mono">{from} – {to}</div>
        </div>
        <button className="btn btn-ghost" style={{ padding: '2px 6px' }} onClick={onClose}><IconX size={12}/></button>
      </div>

      {/* Day / date */}
      <div className="eyebrow" style={{ marginBottom: 4 }}>Tag</div>
      <input
        type="date" className="input" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 8, width: '100%' }}
        value={date} onChange={(e) => setDate(e.target.value)}
      />

      {/* Mode toggle */}
      <div className="seg" style={{ width: '100%', marginBottom: 8 }}>
        <button className={mode === 'project' ? 'on' : ''} onClick={() => setMode('project')} style={{ flex: 1 }}>Projekt-Zeit</button>
        <button className={mode === 'support' ? 'on' : ''} onClick={() => setMode('support')} style={{ flex: 1 }}>Begleitaufgabe</button>
      </div>

      {mode === 'project' && (
        <>
          <select
            className="select" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 6 }}
            value={customerId}
            onChange={(e) => { setCustomerId(e.target.value); if (projectId !== MEETING_VALUE) setProjectId(''); }}
          >
            <option value="">Kunde wählen… (für Meeting optional)</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <select
            className="select" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 6 }}
            value={projectId}
            onChange={(e) => {
              const v = e.target.value;
              if (v === NEW_PROJECT_VALUE) setShowCreateProject(true);
              else setProjectId(v);
            }}
          >
            <option value="">{customerId ? 'Projekt wählen…' : 'Auswahl treffen…'}</option>
            <option value={MEETING_VALUE}>📅 Meeting</option>
            {customerId && customerProjects.length > 0 && (
              <optgroup label="Projekte">
                {customerProjects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </optgroup>
            )}
            {customerId && customerProjects.length === 0 && (
              <option disabled>— Keine aktiven Projekte für diesen Kunden —</option>
            )}
            <option value={NEW_PROJECT_VALUE}>+ Neues Projekt anlegen…</option>
          </select>
        </>
      )}

      <input
        className="input" style={{ padding: '6px 8px', fontSize: 12.5, marginBottom: 8 }}
        placeholder={mode === 'support' ? 'Was hast du gemacht? (Pflicht)' : isMeeting ? 'Meeting-Inhalt (Pflicht)' : 'Was hast du gemacht? (optional)'}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
      />

      <div style={{ display: 'flex', gap: 6 }}>
        <input type="time" className="input" style={{ padding: '4px 8px', fontSize: 12 }} value={from}
               onChange={(e) => setFrom(e.target.value)}/>
        <input type="time" className="input" style={{ padding: '4px 8px', fontSize: 12 }} value={to}
               onChange={(e) => setTo(e.target.value)}/>
      </div>
      {!timeValid && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 4 }}>Endzeit muss nach Startzeit liegen</div>}
      {err && <div className="tiny" style={{ color: 'var(--crit)', marginTop: 6 }}>{err}</div>}

      <button className="btn btn-primary" onClick={save} disabled={busy || !canSave}
              style={{ marginTop: 10, width: '100%', justifyContent: 'center', opacity: (canSave && !busy) ? 1 : 0.45 }}>
        {busy ? 'Speichere…' : 'Änderungen speichern'}
      </button>

      {/* Mit Kolleg:innen teilen — legt unabhängige Kopien bei den Ausgewählten an */}
      {coworkers.length > 0 && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--stroke)' }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Mit Kolleg:innen teilen</div>
          <div style={{
            maxHeight: 132, overflowY: 'auto',
            border: '1px solid var(--stroke)', borderRadius: 8, padding: 4,
            display: 'flex', flexDirection: 'column', gap: 1,
          }}>
            {coworkers.map(p => {
              const on = shareWith.includes(p.id);
              return (
                <label key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 6px', borderRadius: 6, cursor: 'pointer',
                  background: on ? 'var(--bg-hover)' : 'transparent', fontSize: 12.5,
                }}>
                  <input type="checkbox" checked={on} onChange={() => toggleShare(p.id)} style={{ accentColor: 'var(--accent)' }}/>
                  <span style={{
                    width: 18, height: 18, borderRadius: 5, flex: '0 0 18px',
                    background: `linear-gradient(135deg, ${p.avatar_color || '#FF6B4A'}, ${p.avatar_color || '#FF6B4A'}99)`,
                    color: '#fff', display: 'grid', placeItems: 'center', fontSize: 8.5, fontWeight: 700,
                  }}>{p.initials || (p.full_name || p.email || '?').slice(0, 2).toUpperCase()}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.full_name || p.email}
                  </span>
                </label>
              );
            })}
          </div>
          {shareNote && <div className="tiny" style={{ color: 'var(--ok)', marginTop: 6 }}>{shareNote}</div>}
          <button className="btn" onClick={share} disabled={sharing || shareWith.length === 0 || !canSave}
                  style={{ marginTop: 8, width: '100%', justifyContent: 'center', opacity: (shareWith.length > 0 && canSave && !sharing) ? 1 : 0.45 }}>
            {sharing ? 'Kopiere…' : shareWith.length > 0 ? `An ${shareWith.length} Mitarbeiter kopieren` : 'Mitarbeiter auswählen'}
          </button>
          <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.4 }}>
            Jede ausgewählte Person bekommt eine eigene Kopie dieses Eintrags.
          </div>
        </div>
      )}

      {confirmDel ? (
        <div style={{ marginTop: 10, padding: 8, border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8 }}>
          <div className="tiny" style={{ marginBottom: 6 }}>Eintrag wirklich löschen?</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-ghost" style={{ flex: 1, padding: '4px 8px' }} onClick={() => setConfirmDel(false)} disabled={busy}>Abbrechen</button>
            <button className="btn" style={{ flex: 1, padding: '4px 8px', color: 'var(--crit)', borderColor: 'rgba(239,68,68,0.3)' }}
                    onClick={onDelete} disabled={busy}>Löschen</button>
          </div>
        </div>
      ) : (
        <button className="btn" onClick={() => setConfirmDel(true)} disabled={busy}
                style={{ marginTop: 8, width: '100%', justifyContent: 'center', color: 'var(--crit)', borderColor: 'rgba(239,68,68,0.3)' }}>
          Eintrag löschen
        </button>
      )}

      {showCreateProject && (
        <ProjectFormModal
          defaultCustomerId={customerId || undefined}
          onClose={() => setShowCreateProject(false)}
          onSaved={(newProjectId) => {
            if (newProjectId) setProjectId(newProjectId);
            setShowCreateProject(false);
          }}
        />
      )}
    </div>
  );
}

// --- Main page ---------------------------------------------------------------
function TimesPage({ onStartTimer }) {
  const { projects, customers, timeEntries, profiles, currentProfile, projectById, customerById, addTimeEntry, updateTimeEntry, deleteTimeEntry, shareTimeEntry } = useData();
  const coworkers = useMemo(
    () => profiles.filter(p => p.id !== currentProfile.id),
    [profiles, currentProfile.id]
  );
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [composerSlot, setComposerSlot] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [now, setNow] = useState(new Date());

  const isToday = selectedDate === todayIso();

  // Entries for the selected date + current user (replaces todaysEntries)
  const dayEntries = useMemo(
    () => timeEntries.filter(te => te.user_id === currentProfile.id && te.entry_date === selectedDate),
    [timeEntries, selectedDate, currentProfile.id]
  );

  const byProject = useMemo(() => {
    const map = new Map();
    for (const e of dayEntries) {
      const kind = e.kind || (e.project_id ? 'project' : 'support');
      const key = e.project_id ? `proj:${e.project_id}` : `kind:${kind}`;
      const slot = map.get(key) || {
        kind,
        project: e.project_id ? projectById(e.project_id) : null,
        minutes: 0,
      };
      slot.minutes += durationMin(e.from_time, e.to_time);
      map.set(key, slot);
    }
    return [...map.values()].sort((a, b) => b.minutes - a.minutes);
  }, [dayEntries, projectById]);

  const byCustomer = useMemo(() => {
    const map = new Map();
    for (const e of dayEntries) {
      // Meeting → entry.customer_id; Project → project.customer_id; Support → none
      const proj = projectById(e.project_id);
      const cid = e.customer_id || proj?.customer_id || '__none__';
      const m = map.get(cid) || { customer: cid === '__none__' ? null : customerById(cid), minutes: 0 };
      m.minutes += durationMin(e.from_time, e.to_time);
      map.set(cid, m);
    }
    return [...map.values()].sort((a, b) => b.minutes - a.minutes);
  }, [dayEntries, projectById, customerById]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  function handleGridClick(slot) {
    setSelectedId(null);
    const end = Math.min(TOTAL_SLOTS, slot + 2);
    setComposerSlot({ slot, endSlot: end });
  }

  async function handleSave(data) {
    await addTimeEntry({ ...data, entry_date: selectedDate });
    setComposerSlot(null);
  }

  async function handleDelete(id) {
    await deleteTimeEntry(id);
    setSelectedId(null);
  }

  async function handleUpdate(patch) {
    if (!selectedId) return;
    await updateTimeEntry(selectedId, patch);
  }

  const nowMin = now.getHours() * 60 + now.getMinutes();
  const nowInGrid = isToday && nowMin >= START_HOUR * 60 && nowMin <= END_HOUR * 60;
  const nowOffset = ((nowMin - START_HOUR * 60) / SLOT_MIN) * SLOT_PX;

  const totalMin = dayEntries.reduce((acc, e) => acc + durationMin(e.from_time, e.to_time), 0);
  const selected = dayEntries.find(e => e.id === selectedId);
  const selectedProject = selected ? projectById(selected.project_id) : null;

  const dateLabel = formatDayLabel(selectedDate);

  function gotoPrev()  { setSelectedId(null); setComposerSlot(null); setSelectedDate(d => shiftIso(d, -1)); }
  function gotoNext()  { setSelectedId(null); setComposerSlot(null); setSelectedDate(d => shiftIso(d, +1)); }
  function gotoToday() { setSelectedId(null); setComposerSlot(null); setSelectedDate(todayIso()); }

  return (
    <>
      <div className="topbar" style={{ paddingRight: 420 }}>
        <h1>Meine Zeiten</h1>
        <div className="chip">{dayEntries.length} {dayEntries.length === 1 ? 'Eintrag' : 'Einträge'} · {fmtMin(totalMin)}</div>
        <div className="topbar-spacer"/>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <button className="icon-btn" onClick={gotoPrev} title="Vortag"
                  style={{ width: 30, height: 30 }}>
            <IconChevronRight size={14} style={{ transform: 'scaleX(-1)' }}/>
          </button>
          <button className="btn" onClick={gotoToday}
                  title={isToday ? 'Heute' : 'Zu heute springen'}
                  style={{ minWidth: 200, justifyContent: 'center' }}>
            <IconCalendar size={13}/> {dateLabel}
          </button>
          <button className="icon-btn" onClick={gotoNext} title="Folgetag"
                  style={{ width: 30, height: 30 }}>
            <IconChevronRight size={14}/>
          </button>
        </div>
        <button className="btn btn-primary" onClick={() => handleGridClick(timeToSlot('09:00'))}>
          <IconPlus size={14}/> Eintrag
        </button>
      </div>

      <div className="content">
       <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: 20, alignItems: 'start' }}>
        <div className="card card-pad-lg" style={{ maxWidth: 900 }}>
          <div className="card-title">
            <span>Tagesansicht · 07:00 – 20:00</span>
            <span className="sub tiny">Klick auf einen freien Slot, um zu erfassen</span>
          </div>

          <div
            style={{ position: 'relative', paddingLeft: 64 }}
            onClick={() => { setSelectedId(null); setComposerSlot(null); }}
          >
            {Array.from({ length: (END_HOUR - START_HOUR) + 1 }, (_, i) => {
              const hour = START_HOUR + i;
              const top = i * SLOTS_PER_HOUR * SLOT_PX;
              return (
                <div key={hour} style={{ position: 'absolute', left: 0, top, width: '100%' }}>
                  <div style={{
                    position: 'absolute', left: 0, width: 52,
                    fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500,
                    color: 'var(--ink-4)', textAlign: 'right',
                    transform: 'translateY(-6px)',
                  }}>
                    {pad(hour)}:00
                  </div>
                  <div style={{ borderTop: '1px solid var(--stroke)', marginLeft: 64 }}/>
                </div>
              );
            })}

            {Array.from({ length: (END_HOUR - START_HOUR) }, (_, i) => {
              const top = i * SLOTS_PER_HOUR * SLOT_PX + SLOT_PX;
              return (
                <div key={`half-${i}`} style={{
                  position: 'absolute', left: 64, right: 0, top,
                  borderTop: '1px dashed var(--stroke)', opacity: 0.6,
                }}/>
              );
            })}

            <div style={{ position: 'relative', height: TOTAL_SLOTS * SLOT_PX }}>
              {Array.from({ length: TOTAL_SLOTS }, (_, slot) => (
                <div
                  key={slot}
                  onClick={(e) => { e.stopPropagation(); handleGridClick(slot); }}
                  style={{
                    position: 'absolute', left: 0, right: 0,
                    top: slot * SLOT_PX, height: SLOT_PX, cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                />
              ))}

              {dayEntries.map(e => (
                <EntryBlock
                  key={e.id}
                  entry={e}
                  project={projectById(e.project_id)}
                  customer={e.customer_id ? customerById(e.customer_id) : null}
                  selected={e.id === selectedId}
                  onSelect={(id) => { setComposerSlot(null); setSelectedId(id); }}
                  onUpdate={(patch) => updateTimeEntry(e.id, patch)}
                />
              ))}

              {composerSlot && (
                <Composer
                  slot={composerSlot.slot}
                  defaultEndSlot={composerSlot.endSlot}
                  customers={customers}
                  projects={projects}
                  onSave={handleSave}
                  onCancel={() => setComposerSlot(null)}
                />
              )}

              {nowInGrid && (
                <div style={{
                  position: 'absolute', left: -6, right: 0, top: nowOffset,
                  height: 0, borderTop: '2px solid var(--accent)',
                  zIndex: 5, pointerEvents: 'none',
                }}>
                  <div style={{
                    position: 'absolute', left: -10, top: -5,
                    width: 10, height: 10, borderRadius: '50%',
                    background: 'var(--accent)',
                    boxShadow: '0 0 0 3px rgba(255,107,74,0.2)',
                  }}/>
                </div>
              )}

              {selected && (
                <EntryEditor
                  entry={selected}
                  project={selectedProject}
                  customers={customers}
                  projects={projects}
                  coworkers={coworkers}
                  onDelete={() => handleDelete(selected.id)}
                  onClose={() => setSelectedId(null)}
                  onUpdate={handleUpdate}
                  onShare={shareTimeEntry}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right-hand summary */}
        <div className="card card-pad-lg" style={{ position: 'sticky', top: 80 }}>
          <div className="card-title">
            <span>Tagesübersicht</span>
            <span className="sub mono">{fmtMin(totalMin)}</span>
          </div>

          {dayEntries.length === 0 ? (
            <div className="tiny muted" style={{ padding: '8px 0' }}>
              {isToday ? 'Noch keine Einträge heute.' : 'Keine Einträge an diesem Tag.'}
            </div>
          ) : (
            <>
              <div className="eyebrow" style={{ marginBottom: 8 }}>Pro Projekt</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {byProject.map(({ project, kind, minutes }, i) => {
                  const isMeeting = kind === 'meeting';
                  const isSupport = kind === 'support';
                  return (
                    <div key={project?.id || `${kind}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {project ? (
                        <ProjectAvatar project={project} size={26}/>
                      ) : isMeeting ? (
                        <div style={{
                          width: 26, height: 26, borderRadius: 7,
                          background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                          color: '#fff', display: 'grid', placeItems: 'center', fontSize: 13,
                          flex: '0 0 26px',
                        }}>📅</div>
                      ) : (
                        <div style={{
                          width: 26, height: 26, borderRadius: 7,
                          background: 'repeating-linear-gradient(135deg, #4a4f5c, #4a4f5c 5px, #565b69 5px, #565b69 10px)',
                          flex: '0 0 26px',
                        }}/>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {isSupport ? 'Begleitaufgaben' : isMeeting ? 'Meetings' : project.title}
                        </div>
                      </div>
                      <div className="mono tiny" style={{ color: 'var(--ink-2)' }}>{fmtMin(minutes)}</div>
                    </div>
                  );
                })}
              </div>

              <div className="eyebrow" style={{ marginBottom: 8 }}>Pro Kunde</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
                {byCustomer.map(({ customer, minutes }, i) => (
                  <div key={customer?.id || `none-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {customer
                      ? <CustomerAvatar customer={customer} size={26}/>
                      : <div style={{
                          width: 26, height: 26, borderRadius: 6,
                          background: 'var(--bg-hover)',
                          display: 'grid', placeItems: 'center',
                          color: 'var(--ink-4)', fontSize: 11, fontWeight: 600,
                        }}>?</div>}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {customer?.name || 'Ohne Kunde'}
                      </div>
                    </div>
                    <div className="mono tiny" style={{ color: 'var(--ink-2)' }}>{fmtMin(minutes)}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div style={{ borderTop: '1px solid var(--stroke)', paddingTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div className="eyebrow">{isToday ? 'Gesamt heute' : 'Gesamt'}</div>
            <div className="serif" style={{ fontSize: 22, fontWeight: 500, color: totalMin > 0 ? 'var(--accent)' : 'var(--ink-3)' }}>
              {fmtMin(totalMin)}
            </div>
          </div>
        </div>
       </div>
      </div>
    </>
  );
}

export { TimesPage };
